<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, shallowRef } from 'vue';
import type { App } from './babylon';

defineProps<{
  options?: Record<string, unknown>;
}>();

const emits = defineEmits<{
  'bodypart-clicked': [bodyPart: string];
  'info-panel-closed': [];
}>();

const host = ref<HTMLDivElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);

const app = shallowRef<App | null>(null);
const loading = ref(true);

let ro: ResizeObserver | null = null;

const handleBodyPartClicked = (bodyPart: string) => {
  emits('bodypart-clicked', bodyPart);
};

const handleInfoPanelClosed = () => {
  emits('info-panel-closed');
};

const closeBodyPart = () => {
  app.value?.closeInfoPanel();
};

defineExpose({
  closeBodyPart,
});

onMounted(async () => {
  // Babylon is several MB, so it is only downloaded when the game is opened.
  const { App } = await import('./babylon');
  if (!canvas.value) return;

  app.value = new App(canvas.value, handleBodyPartClicked, handleInfoPanelClosed);
  app.value.initScene().then(() => {
    loading.value = false;

    // ResizeObserver handles container size changes (drawer open/close etc.)
    if (host.value && app.value?.engine) {
      ro = new ResizeObserver(() => app.value?.engine.resize());
      ro.observe(host.value);
    }
  });
});

onBeforeUnmount(() => {
  ro?.disconnect();
  ro = null;
  app.value?.dispose.call(app.value);
  app.value = null;
});
</script>

<template>
  <div class="viewport">
    <canvas ref="canvas" class="viewport-canvas" />
    <div v-if="loading" class="overlay">
      <v-progress-circular indeterminate />
    </div>
  </div>
</template>

<style scoped lang="scss">
.viewport {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.viewport-canvas {
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
}
.overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(4px);
}
</style>
